import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ChannelScope } from './ChannelScopeTabs';
import { FieldSourceChip } from './FieldSourceChip';
import type { FieldSource } from './FieldSourceChip';

export interface ScopedFieldLabelProps {
  label: string;
  scope: ChannelScope;
  source: FieldSource;
  /** *(delta 2026-09-23)* `DEFAULT`'s dashed "not saved yet" variant — General tab only. */
  pending?: boolean;
  /** Present only when `source === 'OVERRIDE'` — "Use the general value for <field>" (channel tabs). */
  onUseGeneral?: () => void;
  /** Present only when the field is inherited but the card already has ≥1 override elsewhere
   * (design §7.3 "Some fields overridden" row) — the row-level "Customise" button (channel tabs). */
  onCustomiseRow?: () => void;
  /** *(delta 2026-09-23)* General tab only — "Use platform default" row action. */
  onUseDefault?: () => void;
  /** *(delta 2026-09-23)* General tab only, while `pending` — "Keep your value" (undo). */
  onKeepYours?: () => void;
}

/** `FieldRow`/`SelectField`/`NumberField`/etc. all accept `label: ReactNode` — this composes the
 * plain label with the per-tab provenance chip and its row-level actions (design §7.3/§7.5/§17.2).
 * *(delta 2026-09-23)* Renders on the General tab too — no longer the bare label there — showing the
 * chip only for `DEFAULT` (or pending) and the General-tab reset/undo actions. */
export function ScopedFieldLabel({
  label,
  scope,
  source,
  pending = false,
  onUseGeneral,
  onCustomiseRow,
  onUseDefault,
  onKeepYours,
}: ScopedFieldLabelProps) {
  const { t } = useTranslation('chatbot');

  if (scope === 'GENERAL') {
    if (source !== 'DEFAULT' && !onUseDefault) {
      return <>{label}</>;
    }
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
        <Typography component="span" variant="inherit">
          {label}
        </Typography>
        {source === 'DEFAULT' ? <FieldSourceChip source="DEFAULT" pending={pending} /> : null}
        {onUseDefault ? (
          <Tooltip title={t('provenance.useDefaultAria', { field: label })}>
            <IconButton
              size="small"
              aria-label={t('provenance.useDefaultAria', { field: label })}
              onClick={onUseDefault}
            >
              <RestartAltRoundedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        ) : null}
        {onKeepYours ? (
          <Tooltip title={t('provenance.keepYoursAria', { field: label })}>
            <IconButton
              size="small"
              aria-label={t('provenance.keepYoursAria', { field: label })}
              onClick={onKeepYours}
            >
              <UndoRoundedIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
      <Typography component="span" variant="inherit">
        {label}
      </Typography>
      <FieldSourceChip source={source} />
      {onUseGeneral ? (
        <Tooltip title={t('scope.useGeneralValue', { field: label })}>
          <IconButton
            size="small"
            aria-label={t('scope.useGeneralValue', { field: label })}
            onClick={onUseGeneral}
          >
            <UndoRoundedIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      ) : null}
      {onCustomiseRow ? (
        <Typography
          component="button"
          type="button"
          variant="caption"
          onClick={onCustomiseRow}
          sx={{
            border: 'none',
            background: 'none',
            color: 'primary.main',
            cursor: 'pointer',
            p: 0,
            font: 'inherit',
            fontWeight: 600,
          }}
        >
          {t('scope.customiseField')}
        </Typography>
      ) : null}
    </Stack>
  );
}
